import { produce } from 'immer';
import type { Reducer } from 'redux';
import { type AssetListItem } from '@fleet-bffs/api-contract';
import {
  AssetMapAndDetailsState,
  initialAssetMapAndDetailsState,
} from '~/domain/asset-details-common/store/mapDetails/AssetMapAndDetailsState';
import { handleTagUpdatesInDetails } from '~/domain/asset-details-common/store/mapDetails/reducerHelpers/handleTagUpdatesInDetails';
import AssetMapAndDetailsConstants from '~/domain/asset-details-common/components/AssetMapAndDetails/constants';
import ErrorRecord from '~/data/ErrorRecord';
import type { AssetTagRelationshipsActions } from '~/ducks/asset-tag-relationships/actions';
import * as roboticMowerUpdateActions from '~/ducks/asset-updates/actions';
import mergeDeep from '~/utils/merge-deep/merge-deep';
import { type AssetMapAndDetailsContainerActionTypes } from './actions';

const assetHasId = (assetId: AssetId, asset?: AssetListItem): asset is AssetListItem => {
  if (!asset) {
    return false;
  }

  return assetId === asset.assetDetails.id;
};

const reducer: Reducer<
  AssetMapAndDetailsState,
  AssetMapAndDetailsContainerActionTypes | AssetTagRelationshipsActions
> = (state = initialAssetMapAndDetailsState, action) => {
  return produce(state, (newState) => {
    handleTagUpdatesInDetails(newState, action as AssetTagRelationshipsActions);

    switch (action.type) {
      case AssetMapAndDetailsConstants.SET_ASSET:
        if (state.asset?.assetDetails.id === action.payload.asset.assetDetails.id) {
          return;
        }

        // @ts-expect-error local asset union vs narrower BFF contract
        newState.asset = action.payload.asset;
        newState.isPartial = true;

        break;

      case roboticMowerUpdateActions.updateAsset.type: {
        const { payload } = action as ReturnType<typeof roboticMowerUpdateActions.updateAsset>;

        if (!payload.forceUpdate && !assetHasId(payload.assetId, newState.asset)) {
          return;
        }

        // @ts-expect-error local asset union vs narrower BFF contract
        newState.asset = payload.asset;
        break;
      }

      case AssetMapAndDetailsConstants.SET_OUTDATED: {
        if (action.payload.isOutdated && !newState.asset) {
          return;
        }

        newState.isOutdated = action.payload.isOutdated;
        break;
      }

      case AssetMapAndDetailsConstants.UPDATE_MAINTENANCE_REMINDER: {
        if (newState.asset === undefined || !assetHasId(action.payload.iprId, newState.asset)) {
          return;
        }

        if (newState.asset.maintenance === undefined) {
          newState.asset.maintenance = {};
        }

        newState.asset.maintenance.reminder = action.payload.reminder;
        break;
      }

      case roboticMowerUpdateActions.partialUpdateAsset.type: {
        const { payload } = action as ReturnType<
          typeof roboticMowerUpdateActions.partialUpdateAsset
        >;

        if (!payload.forceUpdate && !assetHasId(payload.assetId, newState.asset)) {
          return;
        }

        // @ts-expect-error mergeDeep returns the wider local union
        newState.asset = mergeDeep(newState.asset, payload.asset);
        break;
      }

      case roboticMowerUpdateActions.updateAssetStatus.type: {
        const { payload } = action as ReturnType<
          typeof roboticMowerUpdateActions.updateAssetStatus
        >;

        if (!assetHasId(payload.assetId, newState.asset)) {
          return;
        }

        // @ts-expect-error BFF contract narrows this Status
        newState.asset.status = payload.status;
        break;
      }

      case roboticMowerUpdateActions.updateAssetHfsActions.type: {
        const {
          payload: { assetId, updated },
        } = action as ReturnType<typeof roboticMowerUpdateActions.updateAssetHfsActions>;

        if (!assetHasId(assetId, newState.asset)) {
          return;
        }

        if (newState.asset.actions === undefined) {
          // No actions at all, perhaps old architecture
          return;
        }

        newState.asset.actions = updated;
        break;
      }

      case roboticMowerUpdateActions.updateAssetPower.type: {
        const { payload } = action as ReturnType<typeof roboticMowerUpdateActions.updateAssetPower>;

        if (!assetHasId(payload.assetId, newState.asset) || !newState.asset.machineData) {
          return;
        }

        newState.asset.machineData.power = payload.power;
        break;
      }

      case roboticMowerUpdateActions.updateAssetFirmware.type: {
        const { payload } = action as ReturnType<
          typeof roboticMowerUpdateActions.updateAssetFirmware
        >;

        if (!assetHasId(payload.assetId, newState.asset) || !newState.asset.firmware) {
          return;
        }

        newState.asset.firmware.version = payload.version;
        break;
      }

      case roboticMowerUpdateActions.updateAssetCSFirmware.type: {
        const { payload } = action as ReturnType<
          typeof roboticMowerUpdateActions.updateAssetCSFirmware
        >;

        if (!assetHasId(payload.assetId, newState.asset) || !newState.asset.roboticMower) {
          return;
        }

        newState.asset.roboticMower.chargingStationFirmwareVersion = payload.version;
        break;
      }

      case roboticMowerUpdateActions.updateAssetLocation.type: {
        const { payload } = action as ReturnType<
          typeof roboticMowerUpdateActions.updateAssetLocation
        >;

        if (!assetHasId(payload.assetId, newState.asset) || !newState.asset.machineData) {
          return;
        }
        newState.asset.machineData.location = payload.location;
        break;
      }

      case roboticMowerUpdateActions.updateAssetConnectivity.type: {
        const { payload } = action as ReturnType<
          typeof roboticMowerUpdateActions.updateAssetConnectivity
        >;

        if (!assetHasId(payload.assetId, newState.asset) || !newState.asset.machineData) {
          return;
        }
        newState.asset.machineData.connectivity = payload.connectivity;
        if (newState.asset.roboticMower?.availableSettings) {
          newState.asset.roboticMower.validSettings = payload.connectivity.connected
            ? newState.asset.roboticMower?.availableSettings
            : [];
        }
        break;
      }

      case AssetMapAndDetailsConstants.UNSET_ASSET:
        delete newState.asset;
        delete newState.error;
        break;

      case AssetMapAndDetailsConstants.GET_ASSET_DETAILS_REQUEST:
        newState.isLoading = true;
        delete newState.error;
        break;

      case AssetMapAndDetailsConstants.GET_ASSET_DETAILS_SUCCESS: {
        newState.isLoading = false;
        newState.isPartial = false;
        break;
      }

      case AssetMapAndDetailsConstants.GET_ASSET_DETAILS_FAILURE:
        newState.isLoading = false;
        newState.error = new ErrorRecord(action.error);
        break;

      case roboticMowerUpdateActions.updateAssetOwnershipData.type: {
        const { payload } = action as ReturnType<
          typeof roboticMowerUpdateActions.updateAssetOwnershipData
        >;

        if (!assetHasId(payload.assetId, newState.asset)) {
          return;
        }

        // @ts-expect-error purchaseDate admits null locally
        newState.asset.ownershipData = payload.ownershipData;
        break;
      }

      default:
        break;
    }
  });
};

export default reducer;
